import React, { useState, useEffect, useId, useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Typography } from '@mui/material';
import Progress from '../item/progress'

export default function TreemapShelterAnalysis(props) {
  const [evacData, setEvacData] = useState([]);
  const [isNoData, setIsNoData] = useState(false);

  // チャートごとの一意のキー
  const chartId = useId();

  // チャート情報を格納
  const setChartData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      setEvacData([]);
      return;
    }

    // 元データの evac 値を取得して最大値を確認
    const rawVals = data.map(item => Number(item.evac_total) || 0);
    const maxVal = Math.max(...rawVals);

    const series_ary = data.map(item => {
      const evacVal = Number(item.evac_total) || 0;
      // 描画用 y は全て 0 の場合に見えるように 1 を使う（ラベルは custom.evac に本来値を保持）
      const displayY = (maxVal === 0) ? 1 : evacVal;
      return {
        x: item.city_name,
        y: displayY,
        fillColor: props.setColor(evacVal).color,
        custom: {
          code: item.city_code,
          name: item.city_name,
          lat: item.area_lat ?? item.dc_lat_num ?? item.lat,
          lng: item.area_lng ?? item.dc_lng_num ?? item.lng,
          evac: evacVal
        }
      }
    });

    // データをステイタスに格納
    setEvacData(series_ary)
  }
  // ローディング終了
  const loadingEnd = () => {
    props.setReady(prev => {
      const updated = { ...prev };
      props.isReadyKey.forEach(key => {
        if (key in updated) {
          updated[key] = true;
        }
      });
      return updated;
    });
  }

  const series = useMemo(() => {
    return [
      {
        data: evacData
      },
    ];
  }, [evacData]);

  // データ更新が完了したらローディング終了
  useEffect(() => {
    loadingEnd()
  }, [evacData]);

  // 定期監視用
  useEffect(() => {
    if (props.treemapData === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.treemapData && props.treemapData.length > 0) {
      setChartData(props.treemapData)
    }
    setIsNoData(false)
    loadingEnd()
    // 依存関係の「dateArray」は削除しないでください。
    // これがないとprops.allRankDataに変化がない場合にローディングが消えなくなるので
  }, [props.dateArray, props.treemapData]);

  useEffect(() => {
    if (props.treemapData === null) {
      setIsNoData(true)
      loadingEnd()
      return
    }
    if (props.treemapData && props.treemapData.length > 0) {
      setChartData(props.treemapData)
    }
    setIsNoData(false)
    loadingEnd()

    return () => {
      ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
    };
  }, []);

  // セル内の文字色、境界線の色を変更
  const updateLabelStyle = (chartContext) => {
    // 境界線の色
    const rects = chartContext.el.querySelectorAll('.apexcharts-treemap-rect');
    rects.forEach((rect) => {
      rect.setAttribute('stroke', '#000000'); // 黒色に変更
    });

    // セル内の文字色
    const labels = chartContext.el.querySelectorAll('.apexcharts-datalabel');
    const data = chartContext.w.config.series[0].data;
    labels.forEach((el, i) => {
      const val = data[i].custom.evac; // ← 値に応じて動的処理
      el.setAttribute('fill', props.setColor(val).font_color);
    });
  }

  const options = useMemo(() => ({
    legend: {
      show: false
    },
    chart: {
      height: '100%',
      type: 'treemap',
      toolbar: {
        show: false,
      },
      events: {
        mounted(chartContext) {
          setTimeout(() => updateLabelStyle(chartContext), 50);
        },
        updated(chartContext) {
          setTimeout(() => updateLabelStyle(chartContext), 50);
        },
        dataPointSelection: (event, chartContext, config) => {
          const seriesIndex = config.seriesIndex;
          const dataPointIndex = config.dataPointIndex;
          if (seriesIndex === -1 || dataPointIndex === -1) return; // クリック対象がない場合ガード
          const clickedItem = series[seriesIndex].data[dataPointIndex];
          const cityCode = clickedItem.custom.code;
          props.clickHandler([cityCode])
        }
      }
    },
    tooltip: {
      enabled: true,
      // theme は使わず、カスタムHTML側で背景色を指定
      custom: function (opts) {
        const dp = opts.w?.config?.series?.[opts.seriesIndex]?.data?.[opts.dataPointIndex] || {};
        const name = dp.custom?.name ?? dp.x ?? '';
        const evac = dp.custom?.evac ?? 0;
        const color = (props.setColor && typeof props.setColor === 'function') ? props.setColor(evac).color : '#00b050';
        return `<div style="padding:6px 8px; font-size:12px; color:#000; background:#ffffff; border-radius:4px; box-shadow:0 1px 4px rgba(0,0,0,0.15); display:flex; align-items:center;">
          <span style="display:inline-block; width:10px; height:10px; background:${color}; border-radius:50%; margin-right:8px; flex:0 0 10px;"></span>
          <span style="white-space:nowrap;">${name}：${evac}</span>
        </div>`;
      }
    },
    fill: {
      type: 'solid',
      colors: []
    },
    dataLabels: {
      enabled: true,
      formatter: function (text, opts) {
        const data = opts.w.config.series[opts.seriesIndex].data
        const value = data[opts.dataPointIndex].y

        const total = data.reduce((sum, d) => sum + d.y, 0)
        // ボックスサイズが小さい場合は表示しない
        if (value / total < 0.03) {
          return ''
        }
        else{
          let cityName = String(text ?? '');
          if (cityName.length > 6) {
            cityName = cityName.slice(0, 2) + '…' + cityName.slice(-3);
          }
          const point = opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[opts.dataPointIndex];
          const evac = point.custom.evac ?? 0;
          return [cityName, evac];
        }
      },
      style: {
        fontSize: '10px',
        fontWeight: 'bold',
        colors: ['#fff'],
        whiteSpace: 'pre-line'
      }
    },
    plotOptions: {
      treemap: {
        distributed: false,
        enableShades: false,
        borderRadius: 1,    // 角丸をなくす
        stroke: {
          width: 1,          // 線の太さ（0で非表示）
          color: '#000000'    // 枠線を黒に
        },
        colorScale: {
          enabled: false
        }
      }
    }
  }), [series]);

  // チャートのデータが更新されたら強制再描画
  useEffect(() => {
    if (series && series.length > 0) {
      ApexCharts.exec(chartId,
        "updateSeries", series);
    }
  }, [series]);

  return (
    <>
      {/* チャート部分 */}
      {!props.isReadyFlg ?
        <Progress />
        :
        isNoData ?
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            対象データがありません
          </Typography>
          :
          <ReactApexChart
            options={options}
            series={series}
            type="treemap"
            height="100%"
            width="100%"
            style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
          />
      }
    </>
  );
}